import { getDevicePref, getUserPref, setUserPref } from '@eventr/common'
import { useMobileDateRangePickerStore } from '@eventr/common-storybook'
import { i18n } from 'i18next'
import { deviceDetect } from 'react-device-detect'
import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { DEVTOOLS_ENABLED } from '../../consts'
import { getDevicePrefName } from '../../utils'

export type TTimeFormats = 'HH:mm' | 'h:mma'

export enum availableLang {
    en_GB = 'en-GB',
    en_US = 'en-US',
    de_DE = 'de-DE',
    es_ES = 'es-ES',
    fr = 'fr',
    it = 'it',
    pt = 'pt',
    ja = 'ja',
    ko = 'ko',
    zh_CN = 'zh-CN',
    zh_TW = 'zh-TW',
}

type TLanguageState = {
    selectedLanguage: availableLang
    setLanguage: (lang: availableLang) => void
    getLanguage: () => availableLang
    getLanguageISO639Code: () => string

    i18nContext: i18n | null
    setI18nContext: (i18n: i18n) => void

    timeFormat: TTimeFormats
    setTimeFormat: (format: TTimeFormats) => void
}

/**
 * Bases 12 or 24 hour clock on language... i hate this but no better way other than allowing the user to set their own preference.
 */
function sortTimeFormat(): TTimeFormats {
    const userPrefTimeFormat: TTimeFormats = getUserPref(
        'account',
        'timeFormat'
    )

    if (userPrefTimeFormat) {
        return userPrefTimeFormat
    }

    /// Defualt from lang???
    try {
        const date = new Date()
        const dateString = date.toLocaleTimeString(window.navigator.language, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeStyle: 'short',
        })

        if (dateString.match(/am|pm/i) || date.toString().match(/am|pm/i)) {
            //12 hour clock
            return 'h:mma'
        } else {
            //24 hour clock
            return 'HH:mm'
        }
    } catch {
        return 'HH:mm'
    }
}

export const useLanguageContext = create<TLanguageState>()(
    subscribeWithSelector(
        devtools(
            (set, get) => {
                const deviceInfo = getDevicePrefName(deviceDetect)
                const currentLanguage =
                    localStorage.getItem('i18nextLng') ||
                    getDevicePref('lang', deviceInfo) ||
                    availableLang.en_GB

                return {
                    selectedLanguage: currentLanguage,
                    setLanguage: (lang: availableLang) => {
                        localStorage.setItem('selectedLanguage', lang)
                        set({ selectedLanguage: lang })
                    },
                    getLanguage: () => get().selectedLanguage,
                    getLanguageISO639Code: () => {
                        switch (get().selectedLanguage) {
                            case availableLang.en_GB:
                                return 'en-GB'
                            case availableLang.en_US:
                                return 'en-US'
                            case availableLang.es_ES:
                                return 'es'
                            case availableLang.de_DE:
                                return 'de'
                            case availableLang.fr:
                                return 'fr'
                            case availableLang.it:
                                return 'it'
                            case availableLang.pt:
                                return 'pt'
                            case availableLang.ja:
                                return 'ja'
                            case availableLang.ko:
                                return 'ko'
                            case availableLang.zh_CN:
                            case availableLang.zh_TW:
                                return 'zh'
                            default:
                                return 'en-GB'
                        }
                    },

                    i18nContext: null,
                    setI18nContext: (i18n) => {
                        set({ i18nContext: i18n })
                    },

                    timeFormat: sortTimeFormat(),
                    setTimeFormat: (format: TTimeFormats) => {
                        setUserPref('account', 'timeFormat', format)
                        set({ timeFormat: format })
                    },
                }
            },
            { name: 'language-context', enabled: DEVTOOLS_ENABLED }
        )
    )
)
useLanguageContext.subscribe(
    (state) => state.selectedLanguage,
    (state) => {
        if (state) {
            useMobileDateRangePickerStore.setState({ monthsToShow: [] })
            useMobileDateRangePickerStore
                .getState()
                .initializeMonthsToShow(
                    useLanguageContext.getState().getLanguageISO639Code()
                )
        }
    },
    { fireImmediately: true }
)

export const langToMapping = (lang: string): availableLang => {
    const foundLang = Object.values(availableLang).find(
        (value) => value === lang
    )

    return foundLang ? (foundLang as availableLang) : availableLang.en_GB
}
